/**
 * Custom hook for printing attention reports
 */
import { useCallback } from 'react';
import { renderSupervisorSignatureHtml, renderTestigoInfoHtml } from '../../utils/sealUtils';

export const usePrintAtencion = () => {
  const generatePrintContent = useCallback((
    requerimiento,
    atencion,
    testigo,
    datosAtencion,
    solicitudes,
    platformConfig,
    userName,
    sealData
  ) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Acta de Atención - ${requerimiento?.codigo}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            font-size: 11px; 
            line-height: 1.4;
            padding: 20px;
            color: #333;
          }
          .header-container {
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 2px solid #9d8272;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .header-logo {
            max-height: 50px;
            max-width: 120px;
            object-fit: contain;
          }
          .header-title {
            text-align: center;
            flex: 1;
          }
          .header-title h1 {
            font-size: 18px;
            color: #9d8272;
            margin-bottom: 5px;
          }
          .header-title p {
            font-size: 12px;
            color: #666;
          }
          .header-spacer {
            width: 120px;
          }
          .section { 
            margin-bottom: 15px; 
            border: 1px solid #ddd; 
            border-radius: 5px;
            overflow: hidden;
          }
          .section-title { 
            background: #f5f5f5; 
            padding: 8px 12px; 
            font-weight: bold; 
            font-size: 12px;
            border-bottom: 1px solid #ddd;
            color: #9d8272;
          }
          .section-content { padding: 12px; }
          .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
          .grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
          .field { margin-bottom: 8px; }
          .field-label { font-size: 9px; color: #888; text-transform: uppercase; }
          .field-value { font-weight: 500; }
          .solicitud-item { 
            border: 1px solid #eee; 
            padding: 10px; 
            margin-bottom: 10px; 
            border-radius: 4px;
            background: #fafafa;
          }
          .solicitud-header { 
            font-weight: bold; 
            color: #9d8272; 
            margin-bottom: 8px;
            font-size: 11px;
          }
          .signatures { 
            margin-top: 40px; 
            display: grid; 
            grid-template-columns: repeat(2, 1fr); 
            gap: 40px;
            align-items: end;
          }
          .signature-box { 
            text-align: center;
          }
          .signature-label { font-size: 10px; color: #666; }
          .footer { 
            margin-top: 30px; 
            text-align: center; 
            font-size: 9px; 
            color: #888;
            border-top: 1px solid #ddd;
            padding-top: 10px;
          }
          @media print {
            body { padding: 10px; }
            .section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header-container">
          <div class="header-logo-container">
            ${platformConfig?.logo_url 
              ? `<img src="${platformConfig.logo_url}" alt="${platformConfig.company_name}" class="header-logo" />`
              : `<span style="font-weight: bold; color: #9d8272; font-size: 14px;">${platformConfig?.company_name || 'IMAGINA'}</span>`
            }
          </div>
          <div class="header-title">
            <h1>ACTA DE ATENCIÓN ${atencion?.codigo || 'A' + requerimiento?.codigo}</h1>
            <p>Requerimiento #${requerimiento?.codigo} | ${requerimiento?.proyecto_nombre || 'N/A'}</p>
          </div>
          <div class="header-spacer"></div>
        </div>

        <div class="section">
          <div class="section-title">📋 Información del Requerimiento</div>
          <div class="section-content">
            <div class="grid">
              <div class="field">
                <div class="field-label">Proyecto</div>
                <div class="field-value">${requerimiento?.proyecto_nombre || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Unidad/Depto</div>
                <div class="field-value">${requerimiento?.departamento || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Etapa</div>
                <div class="field-value">${requerimiento?.etapa || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Código</div>
                <div class="field-value">#${requerimiento?.codigo || '-'}</div>
              </div>
            </div>
            <div class="grid" style="margin-top: 10px;">
              <div class="field">
                <div class="field-label">Titular</div>
                <div class="field-value">${requerimiento?.cliente_nombre || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Teléfono Titular</div>
                <div class="field-value">${requerimiento?.cliente_telefono || '-'}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">👤 Datos del Testigo</div>
          <div class="section-content">
            <div class="grid">
              <div class="field">
                <div class="field-label">Nombre</div>
                <div class="field-value">${testigo?.nombre || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Doc. Identidad</div>
                <div class="field-value">${testigo?.dni || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Teléfono</div>
                <div class="field-value">${testigo?.telefono || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Correo</div>
                <div class="field-value">${testigo?.email || '-'}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">📅 Datos de la Atención</div>
          <div class="section-content">
            <div class="grid">
              <div class="field">
                <div class="field-label">Fecha Atención</div>
                <div class="field-value">${datosAtencion?.fecha || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Hora Inicio</div>
                <div class="field-value">${datosAtencion?.horaInicio || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Hora Fin</div>
                <div class="field-value">${datosAtencion?.horaFin || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Supervisor</div>
                <div class="field-value">${userName || 'No asignado'}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">🔧 Solicitudes Atendidas (${solicitudes?.length || 0})</div>
          <div class="section-content">
            ${(solicitudes || []).length > 0 ? solicitudes.map((sol, idx) => `
              <div class="solicitud-item">
                <div class="solicitud-header">#${idx + 1} - ${sol.solicitud?.categoria || sol.categoria || 'Sin categoria'}</div>
                <div class="grid">
                  <div class="field">
                    <div class="field-label">Categoría</div>
                    <div class="field-value">${sol.solicitud?.categoria || sol.categoria || '-'}</div>
                  </div>
                  <div class="field">
                    <div class="field-label">Lugar</div>
                    <div class="field-value">${sol.solicitud?.lugar || sol.lugar || '-'}</div>
                  </div>
                  <div class="field">
                    <div class="field-label">Item</div>
                    <div class="field-value">${sol.solicitud?.item || sol.item || '-'}</div>
                  </div>
                  <div class="field">
                    <div class="field-label">Falla</div>
                    <div class="field-value">${sol.solicitud?.falla || sol.falla || '-'}</div>
                  </div>
                </div>
                <div class="grid-2" style="margin-top: 8px;">
                  <div class="field">
                    <div class="field-label">Solución Aplicada</div>
                    <div class="field-value">${sol.solucion_aplicada || '-'}</div>
                  </div>
                  <div class="field">
                    <div class="field-label">Estado</div>
                    <div class="field-value">${sol.estado?.replace(/_/g, ' ') || 'Pendiente'}</div>
                  </div>
                </div>
              </div>
            `).join('') : '<p style="text-align: center; color: #888; padding: 20px;">Sin solicitudes registradas</p>'}
          </div>
        </div>

        <div style="margin-top: 20px; padding: 12px 15px; border-top: 1px solid #e0e0e0;">
          <p style="font-size: 9px; font-weight: 600; color: #aaaaaa; margin: 0 0 6px 0;">Notas</p>
          <ol style="font-size: 8px; color: #aaaaaa; margin: 0; padding-left: 16px; line-height: 1.5;">
            <li style="margin-bottom: 4px;">Por la presente, informo a ${platformConfig?.company_name || 'IMAGINA'} que cuento con la autorización expresa del propietario para firmar y dar validez a la información consignada en este documento.</li>
            <li>Por la presente, al proporcionar mis datos autorizo de manera expresa a ${platformConfig?.company_name || 'IMAGINA'} a almacenarlos y hacer uso de los mismos con la finalidad de enviar información, publicidad, encuestas y comunicaciones con fines estratégicos de marketing físico y digital.</li>
          </ol>
        </div>

        <div class="signatures">
          <div class="signature-box">
            ${renderSupervisorSignatureHtml(requerimiento)}
          </div>
          <div class="signature-box">
            ${(() => {
              const estado = atencion?.acta_estado || '';
              const actaAprobada = ['aceptado', 'aprobado_correo', 'aprobado_whatsapp'].includes(estado);
              return renderTestigoInfoHtml(testigo, sealData, false, actaAprobada);
            })()}
          </div>
        </div>

        <div class="footer">
          <p>Documento generado el ${new Date().toLocaleString('es-PE')} | ${platformConfig?.platform_name || 'Sistema de Postventa'} ${platformConfig?.company_name || 'IMAGINA'}</p>
        </div>
      </body>
      </html>
    `;
  }, []);

  const handlePrint = useCallback((
    requerimiento,
    atencion,
    testigo,
    datosAtencion,
    solicitudes,
    platformConfig,
    userName,
    sealData
  ) => {
    const printContent = generatePrintContent(
      requerimiento,
      atencion,
      testigo,
      datosAtencion,
      solicitudes,
      platformConfig,
      userName,
      sealData
    );

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      
      setTimeout(() => {
        printWindow.print();
      }, 500);
      return true;
    }
    
    return false;
  }, [generatePrintContent]);

  return { handlePrint, generatePrintContent };
};

export default usePrintAtencion;
